import React from 'react';
import {Store} from '../../models/store.model';
import {AppText, AppCardTitle, AppBodyText, AppLabelText, AppSectionTitle, AppTitle} from '../../../../Presentation/components/ui/Typography';
import {MapPin, Building, Package, FileText, Calendar, Clock} from 'lucide-react';

interface StoreAdditionalInfoProps {
  store: Store;
}

export const StoreAdditionalInfo: React.FC<StoreAdditionalInfoProps> = ({store}) => {
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) {return 'غير محدد';}

    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return dateString;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'bg-green-100 text-green-800 border-green-200';
      case 'inactive': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getInventoryTypeColor = (inventory: string) => {
    switch (inventory?.toLowerCase()) {
      case 'normal': return 'bg-brand-100 text-brand-800 border-brand-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="space-y-4">
      <AppCardTitle className="border-b pb-2">معلومات إضافية</AppCardTitle>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Area */}
        {store.area && (
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200">
            <div className="flex items-center gap-3 mb-2">
              <MapPin className="w-5 h-5 text-purple-600" />
              <AppLabelText className="text-purple-700 font-medium">المنطقة</AppLabelText>
            </div>
            <AppSectionTitle className="font-bold text-purple-900">
              {store.area}
            </AppSectionTitle>
          </div>
        )}

        {/* Status */}
        {store.status && (
          <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-4 rounded-lg border border-indigo-200">
            <div className="flex items-center gap-3 mb-2">
              <Building className="w-5 h-5 text-indigo-600" />
              <AppLabelText className="text-indigo-700 font-medium">حالة المتجر</AppLabelText>
            </div>
            <span className={`px-3 py-1 rounded-full text-sm font-bold border ${getStatusColor(store.status)}`}>
              {store.status === 'active' ? 'نشط' : store.status === 'inactive' ? 'غير نشط' : store.status}
            </span>
          </div>
        )}

        {/* Inventory Type */}
        {store.inventory && (
          <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 p-4 rounded-lg border border-cyan-200">
            <div className="flex items-center gap-3 mb-2">
              <Package className="w-5 h-5 text-cyan-600" />
              <AppLabelText className="text-cyan-700 font-medium">نوع المخزون</AppLabelText>
            </div>
            <span className={`px-3 py-1 rounded-full text-sm font-bold border ${getInventoryTypeColor(store.inventory)}`}>
              {store.inventory === 'normal' ? 'عادي' : store.inventory}
            </span>
          </div>
        )}

        {/* Stored Quantity Available */}
        {store.storedQuantityAvailable !== undefined && (
          <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 p-4 rounded-lg border border-emerald-200">
            <div className="flex items-center gap-3 mb-2">
              <Package className="w-5 h-5 text-emerald-600" />
              <AppLabelText className="text-emerald-700 font-medium">الكمية المخزنة</AppLabelText>
            </div>
            <AppTitle className="font-bold text-emerald-900">
              {store.storedQuantityAvailable} قطعة
            </AppTitle>
          </div>
        )}

        {/* Created Date */}
        {store.created_at && (
          <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-lg border border-orange-200">
            <div className="flex items-center gap-3 mb-2">
              <Calendar className="w-5 h-5 text-orange-600" />
              <AppLabelText className="text-orange-700 font-medium">تاريخ الإنشاء</AppLabelText>
            </div>
            <AppBodyText className="font-bold text-orange-900">
              {formatDate(store.created_at)}
            </AppBodyText>
          </div>
        )}

        {/* Updated Date */}
        {store.updated_at && (
          <div className="bg-gradient-to-br from-teal-50 to-teal-100 p-4 rounded-lg border border-teal-200">
            <div className="flex items-center gap-3 mb-2">
              <Clock className="w-5 h-5 text-teal-600" />
              <AppLabelText className="text-teal-700 font-medium">آخر تحديث</AppLabelText>
            </div>
            <AppBodyText className="font-bold text-teal-900">
              {formatDate(store.updated_at)}
            </AppBodyText>
          </div>
        )}
      </div>

      {/* Notes */}
      {store.notes && (
        <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-4 rounded-lg border border-amber-200">
          <div className="flex items-center gap-3 mb-3">
            <FileText className="w-5 h-5 text-amber-600" />
            <AppLabelText className="text-amber-700 font-medium">ملاحظات</AppLabelText>
          </div>
          <AppText className="text-amber-900 leading-relaxed">
            {store.notes}
          </AppText>
        </div>
      )}
    </div>
  );
};
